import { noop } from 'lodash';
import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { Camera } from '@zocdoc-frontend-common/assets-icons';
import { sharpSansMedium } from '@zocdoc-frontend-common/styles-typography';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import {
    black,
    yellow,
    grey25 as grey,
    grey50,
    navy,
    white,
} from '@zocdoc/branding/colors';
import PhotoManipulator from './PhotoManipulator';

/* Set max size for longest side of uploaded image */
const MAX_SIZE = 3120;

const Container = styled.div`
    position: relative;
    width: 100%;
    max-width: 330px;
    min-width: 305px;
    height: 220px;
    margin: auto;
`;

const Label = styled.label`
    cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
    display: block;
    position: relative;
`;

const Input = styled.input`
    box-sizing: content-box;
    cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
    display: block;
    height: 180px;
    overflow: hidden;
    padding-left: 30%;
    padding-top: 40px;
    position: relative;
    width: 70%;
`;

const containerCss = css`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    border-radius: 15px;
`;

const CaptureContainer = styled.div`
    ${containerCss}
`;

export const Canvas = styled.canvas`
    ${containerCss}
    z-index: 1;
`;

const Capture = styled.div`
    ${containerCss}
    background: #FFFFFF;
    border: 1px solid ${(p) => (p.invertColor ? black : grey50)};
    display: block;
    pointer-events: none;
`;

/* eslint-disable no-nested-ternary */
export const IconContainer = styled.div`
    position: absolute;
    top: 76px;
    left: 41%;
    height: 64px;
    width: 64px;
    pointer-events: none;
    background-color: ${(p) =>
        p.enabled ? (p.invertColor ? navy : yellow) : grey};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(p) => (p.enabled && !p.invertColor ? navy : white)};
`;
/* eslint-enable no-nested-ternary */

const Text = styled.span`
    ${sharpSansMedium(15, 20, 1)}
    position: absolute;
    top: 158px;
    left: ${(p) => p.textMargin};
    right: ${(p) => p.textMargin};
    text-align: center;
`;

/**
 * @param {boolean} enabled
 * @param {number} imageQuality
 * @param {function(): void} onChangeCallback
 * @param {function(): void} onChangeStartCallback
 * @param {function(): void} onClickScanCard
 * @param {string} text
 * @param {string} textMargin
 * @returns {JSX.Element}
 */
class PhotoCapture extends Component {
    static defaultProps = {
        onChangeCallback: noop,
        onChangeStartCallback: noop,
        onClickScanCard: noop,
        enabled: true,
        imageQuality: 0.5,
        textMargin: '24px',
    };

    photoManipulator = null;

    constructor(props) {
        super(props);
        this.state = {
            photoBtnPressed: false,
            photoBtnTimer: null,
        };

        // For testing
        if (props.forceInverted) {
            this.state.photoBtnPressed = true;
        }
    }

    componentWillUnmount() {
        if (this.state.photoBtnTimer) {
            window.clearTimeout(this.state.photoBtnTimer);
        }
    }

    handleTouchStart = (e) => {
        this.setState({
            photoBtnPressed: true,
            photoBtnTimer: window.setTimeout(() => this.handleTouchEnd(), 500),
        });
        this.props.onClickScanCard(e);
    };

    handleTouchEnd = () => {
        const timer = this.state.photoBtnTimer;
        this.setState({
            photoBtnPressed: false,
            photoBtnTimer: null,
        });
        if (timer) {
            window.clearTimeout(timer);
        }
    };

    render() {
        const disabled = !this.props.enabled;

        return (
            <Container
                data-test="photo-capture"
                {...filterToValidAttributes(this.props)}
            >
                <Label disabled={disabled}>
                    <Input
                        accept="image/*; capture=camera"
                        aria-label="Upload an image"
                        data-test="photo-capture-input"
                        data-uem-id="photo-capture-input"
                        disabled={disabled}
                        onChange={this.onFileChange}
                        type="file"
                    />

                    <Canvas
                        className="exclude-from-playback"
                        data-test="photo-capture-canvas"
                        ref={(node) => {
                            this.canvas = node;
                        }}
                        onTouchEnd={this.handleTouchEnd}
                        onTouchStart={this.handleTouchStart}
                    />

                    <CaptureContainer>
                        <Capture invertColor={this.state.photoBtnPressed} />
                    </CaptureContainer>

                    <IconContainer
                        enabled={this.props.enabled}
                        invertColor={this.state.photoBtnPressed}
                    >
                        <Camera color="currentColor" height={30} />
                    </IconContainer>

                    <Text
                        data-test="photo-capture-text"
                        textMargin={this.props.textMargin}
                    >
                        {this.props.text}
                    </Text>
                </Label>
            </Container>
        );
    }

    onFileChange = (e) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            this.setImageData(event.target.result);
        };

        if (e.target.files[0]) {
            reader.readAsDataURL(e.target.files[0]);

            this.props.onChangeStartCallback();
        }
    };

    setImageData(imageSrc, ignoreCallback) {
        const img = new Image();
        img.onload = () => {
            const { canvas } = this;

            this.photoManipulator = new PhotoManipulator(img);
            this.photoManipulator
                .setMaxSize(MAX_SIZE)
                .setQuality(this.props.imageQuality);

            this.photoManipulator.drawToCanvas(canvas);

            if (!ignoreCallback) {
                this.props.onChangeCallback();
            }
        };

        img.src = imageSrc;
    }

    getImageData(includePrefix) {
        if (this.photoManipulator === null) {
            return null;
        }

        const { canvas } = this;
        const dataUrl = this.photoManipulator.getDataURL(canvas);

        return includePrefix ? dataUrl : this.removeDataUrlPrefix(dataUrl);
    }

    removeDataUrlPrefix(dataUrl) {
        return dataUrl.split(',')[1];
    }
}

export { PhotoCapture as default, PhotoCapture };
